/**
 * The stash — the read side of `{ kind: 'stash', slots: n }` (issue #5).
 *
 * `Progression.stashSlots()` had zero callers, there was no stash array anywhere
 * that survived a death, and the loadout panel advertised "8 weapon slots ·
 * stash is unlimited" over a fixed 8-entry array whose spare slots were
 * permanently null. Three unlocks costing 1120 Ash between them bought a number
 * nothing read.
 *
 * Two decisions shape this file.
 *
 * **The stash lives on the profile, not the run.** That is the entire promise of
 * the unlock — "+3 slots that survive death" — and `RunState.inventory.stash`
 * cannot keep it, because the run object is discarded by `endRunAndRestart`.
 * `ProgressionState.stash` persists through `save.ts` with everything else that
 * outlives a body.
 *
 * **It stores definitions, not instances.** `WeaponInstance` (#12) carries a
 * rolled identity that belongs to combat/ and dies with the run; serialising one
 * would put a combat type into the save file and make every future change to
 * that type a migration. A `StashItem` is the durable subset — what it is, and
 * what was screwed into it — and the integrator re-instantiates on withdrawal.
 * The cost is honest and worth stating: a stashed weapon comes back as the same
 * weapon with the same modules, not with the same roll.
 *
 * Access is gated on **rest nodes** (#6), per the decision recorded on issue #5:
 * sanctums at band boundaries are guaranteed, rest nodes are optional in-band
 * finds, and gating on the guaranteed one would make the stash a service every
 * run gets rather than a reward for exploring sideways. Until rest nodes exist,
 * `atRestNode` is false everywhere and the stash reads "locked" — which is the
 * truth, and says so, rather than being silently open.
 *
 * Decoupled by design: weapons and modules are plain string ids, as everywhere
 * else in meta/.
 */

import type { Progression } from './progression';

export type StashItemKind = 'weapon' | 'module';

export interface StashItem {
  /**
   * Unique within the stash and stable across save/load, so a UI row and a
   * withdrawal request can name the same object. Not a weapon instance id:
   * instance ids are regenerated every run.
   */
  id: string;
  /** Definition id — `WeaponDef.id` or `ModuleDef.id`. */
  defId: string;
  kind: StashItemKind;
  /** Display name, captured at deposit so the panel needs no catalogue. */
  label: string;
  /** Weapons only: installed module definition ids, restored with the item. */
  modules: string[];
}

/** Why the stash is closed, in the words a panel should print. */
export type StashLockReason = 'open' | 'no-slots' | 'not-at-rest-node';

export interface StashAccess {
  open: boolean;
  reason: StashLockReason;
  /** Player-facing sentence. Empty when open. */
  hint: string;
}

export const STASH_LOCK_HINTS: Readonly<Record<StashLockReason, string>> = {
  open: '',
  'no-slots': 'No stash yet — buy Stash Slots I (120 Ash) on the run-setup screen.',
  'not-at-rest-node': 'Stash locked — reachable only at a rest node.',
};

/**
 * The gate, as one function so there is exactly one definition of "open".
 *
 * Order matters: a profile with no slots is told it owns no stash rather than
 * that it is standing in the wrong place, because the second sentence would send
 * a player hunting for a rest node that would not help them.
 */
export function stashAccess(capacity: number, atRestNode: boolean): StashAccess {
  if (capacity <= 0) {
    return { open: false, reason: 'no-slots', hint: STASH_LOCK_HINTS['no-slots'] };
  }
  if (!atRestNode) {
    return { open: false, reason: 'not-at-rest-node', hint: STASH_LOCK_HINTS['not-at-rest-node'] };
  }
  return { open: true, reason: 'open', hint: '' };
}

export interface StashOpResult {
  ok: boolean;
  /** Present on failure, in the words a button should print. */
  reason?: string;
  item?: StashItem;
}

/** What the loadout panel's stash column renders. */
export interface StashVM {
  capacity: number;
  used: number;
  items: readonly StashItem[];
  open: boolean;
  /** Non-empty when `open` is false. The panel prints this instead of a slot grid. */
  hint: string;
}

/**
 * A view over `ProgressionState.stash`.
 *
 * Constructed per interaction rather than held, mirroring `Factions` over
 * `FactionState`: the array is the state, this is only the rules about it. That
 * is what keeps a stash mutation and a save of the profile the same write.
 */
export class Stash {
  constructor(private readonly prog: Progression) {}

  /** The one call site of `Progression.stashSlots()`. */
  capacity(): number {
    return this.prog.stashSlots();
  }

  items(): readonly StashItem[] {
    return this.prog.state.stash;
  }

  free(): number {
    return Math.max(0, this.capacity() - this.prog.state.stash.length);
  }

  access(atRestNode: boolean): StashAccess {
    return stashAccess(this.capacity(), atRestNode);
  }

  view(atRestNode: boolean): StashVM {
    const access = this.access(atRestNode);
    return {
      capacity: this.capacity(),
      used: this.prog.state.stash.length,
      items: this.items(),
      open: access.open,
      hint: access.hint,
    };
  }

  /**
   * Put something in. Refuses rather than truncating: a deposit that silently
   * drops the item is the worst outcome available, and a full stash the player
   * can read is a decision they can act on.
   */
  deposit(item: Omit<StashItem, 'id'> & { id?: string }, atRestNode: boolean): StashOpResult {
    const access = this.access(atRestNode);
    if (!access.open) return { ok: false, reason: access.hint };
    const list = this.prog.state.stash;
    if (list.length >= this.capacity()) {
      return { ok: false, reason: `stash is full (${list.length}/${this.capacity()})` };
    }
    const stored: StashItem = {
      id: item.id && !list.some((s) => s.id === item.id) ? item.id : this.nextId(item.defId),
      defId: item.defId,
      kind: item.kind,
      label: item.label,
      modules: [...(item.modules ?? [])],
    };
    list.push(stored);
    return { ok: true, item: stored };
  }

  /** Take something out. The item leaves the profile — a withdrawal is a move. */
  withdraw(id: string, atRestNode: boolean): StashOpResult {
    const access = this.access(atRestNode);
    if (!access.open) return { ok: false, reason: access.hint };
    const list = this.prog.state.stash;
    const i = list.findIndex((s) => s.id === id);
    if (i < 0) return { ok: false, reason: `nothing in the stash with id ${id}` };
    const [item] = list.splice(i, 1);
    return { ok: true, item };
  }

  /**
   * Deterministic and collision-free without a counter in the save: the lowest
   * unused ordinal for this definition. A counter would have to be migrated and
   * could drift; scanning a list capped at 10 entries costs nothing.
   */
  private nextId(defId: string): string {
    const taken = new Set(this.prog.state.stash.map((s) => s.id));
    for (let n = 1; ; n++) {
      const id = `${defId}#${n}`;
      if (!taken.has(id)) return id;
    }
  }
}

/**
 * Repair a stash loaded from disk. Content drift, not shape drift — the same
 * contract `normaliseState` keeps for everything else, and for the same reason:
 * a renamed weapon id must cost the player that item, never the profile.
 *
 * Over-capacity lists are kept rather than trimmed. Capacity is unlock-derived
 * and can only shrink through drift in the unlock table itself; deleting a
 * player's gear to satisfy a number that moved is not a repair. `deposit`
 * refuses while over capacity, which drains the overflow through play.
 */
export function normaliseStash(raw: unknown): StashItem[] {
  if (!Array.isArray(raw)) return [];
  const out: StashItem[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const s = r as Partial<StashItem>;
    if (typeof s.defId !== 'string' || !s.defId) continue;
    const kind: StashItemKind = s.kind === 'module' ? 'module' : 'weapon';
    let id = typeof s.id === 'string' && s.id ? s.id : `${s.defId}#1`;
    while (seen.has(id)) id = `${id}'`;
    seen.add(id);
    out.push({
      id,
      defId: s.defId,
      kind,
      label: typeof s.label === 'string' && s.label ? s.label : s.defId,
      modules: Array.isArray(s.modules) ? s.modules.filter((m) => typeof m === 'string') : [],
    });
  }
  return out;
}
